import type { CardType } from './payment.types';
import {
    addLosslessNumbers,
    formatLosslessNumberFixed,
    formatLosslessUsd,
    LosslessNumber,
    multiplyLosslessNumbers,
    roundLosslessNumber,
} from './lossless-json-codec';

export function losslessUsdAmountToSafeIntegerCents(amount: LosslessNumber): number {
    const match = /^(?<whole>\d+)(?:\.(?<fraction>\d{1,2}))?$/u.exec(amount.value);
    if (match?.groups === undefined) {
        throw new TypeError('USD amount must have at most two decimal places');
    }
    const cents = BigInt(`${match.groups.whole}${(match.groups.fraction ?? '').padEnd(2, '0')}`);
    if (cents > BigInt(Number.MAX_SAFE_INTEGER)) {
        throw new RangeError('USD amount is out of range');
    }
    const safeIntegerCents = Number(cents);
    if (!Number.isSafeInteger(safeIntegerCents)) {
        throw new RangeError('USD amount is out of range');
    }
    return safeIntegerCents;
}

export interface PaymentConfigurationDisplayInput {
    readonly amount: LosslessNumber;
    readonly cardType: CardType | null;
    readonly allowSurcharge: boolean;
    readonly allowConvenienceFee: boolean;
    readonly surchargePercent: LosslessNumber | null;
    readonly convenienceFee: LosslessNumber | null;
}

export interface PaymentConfigurationDisplayValues {
    readonly surchargeAmount?: LosslessNumber;
    readonly convenienceFeeAmount?: LosslessNumber;
    readonly totalFeeAmount?: LosslessNumber;
    readonly totalAmountPaid?: LosslessNumber;
    readonly surchargePercentLabel?: string;
    readonly surchargeAmountLabel?: string;
    readonly convenienceFeeLabel?: string;
    readonly totalAmountLabel?: string;
}

export function buildPaymentConfigurationDisplayValues(
    input: PaymentConfigurationDisplayInput
): PaymentConfigurationDisplayValues {
    const zero = new LosslessNumber('0');
    if (input.amount.value.startsWith('-')) {
        return {};
    }
    if (input.amount.value === '0') {
        return {
            surchargeAmount: zero,
            convenienceFeeAmount: zero,
            totalFeeAmount: zero,
            totalAmountPaid: zero,
        };
    }

    const convenienceFeeAmount = input.allowConvenienceFee ? (input.convenienceFee ?? zero) : zero;
    if (input.allowSurcharge && input.cardType === null) {
        return {
            convenienceFeeAmount,
            convenienceFeeLabel:
                input.allowConvenienceFee && input.convenienceFee !== null
                    ? formatLosslessUsd(convenienceFeeAmount)
                    : undefined,
        };
    }

    if (input.allowSurcharge && input.cardType === 'UNKNOWN') {
        const totalAmountPaid = addLosslessNumbers([input.amount, convenienceFeeAmount]);
        return {
            surchargeAmount: zero,
            convenienceFeeAmount,
            totalFeeAmount: convenienceFeeAmount,
            totalAmountPaid,
            convenienceFeeLabel:
                input.allowConvenienceFee && input.convenienceFee !== null
                    ? formatLosslessUsd(convenienceFeeAmount)
                    : undefined,
        };
    }

    const surchargeIsWaivedForDebitCard: boolean =
        input.allowSurcharge && input.cardType === 'DEBIT';
    const effectiveSurchargePercent =
        input.allowSurcharge && !surchargeIsWaivedForDebitCard
            ? (input.surchargePercent ?? zero)
            : zero;
    const surchargeAmount = roundLosslessNumber(
        multiplyLosslessNumbers(
            multiplyLosslessNumbers(effectiveSurchargePercent, input.amount),
            new LosslessNumber('0.01')
        ),
        2
    );
    const totalFeeAmount = addLosslessNumbers([surchargeAmount, convenienceFeeAmount]);
    const totalAmountPaid = addLosslessNumbers([input.amount, totalFeeAmount]);

    return {
        surchargeAmount,
        convenienceFeeAmount,
        totalFeeAmount,
        totalAmountPaid,
        surchargePercentLabel: input.allowSurcharge
            ? surchargeIsWaivedForDebitCard
                ? '—'
                : `${formatLosslessNumberFixed(effectiveSurchargePercent, 2)}%`
            : undefined,
        surchargeAmountLabel: input.allowSurcharge
            ? surchargeIsWaivedForDebitCard
                ? '—'
                : formatLosslessUsd(surchargeAmount)
            : undefined,
        convenienceFeeLabel:
            input.allowConvenienceFee && input.convenienceFee !== null
                ? formatLosslessUsd(convenienceFeeAmount)
                : undefined,
        totalAmountLabel:
            input.allowSurcharge || input.allowConvenienceFee
                ? formatLosslessUsd(totalAmountPaid)
                : undefined,
    };
}
